/**
 * One choosable option in the /build configurator.
 *
 * No `'use client'` on purpose. Its only importer is the island, so it is
 * already on the client; re-declaring the directive would mint a second entry
 * into the client graph for nothing. Same for BuildSummary.
 *
 * The control is a REAL `<input>` inside its `<label>`, visually hidden. That
 * buys native radiogroup behaviour from the fieldset — arrow keys move between
 * options, Space toggles an add-on, the accessible name and checked state are
 * announced without a line of ARIA. A `<div role="radio">` would have to
 * reimplement all of it, and the roving-tabindex half is the part everyone
 * forgets.
 *
 * Selected state carries FOUR signals, only one of which is hue:
 *   1. `border-2 border-[var(--color-selected)]` — guarded to WCAG 1.4.11's
 *      3:1 against BOTH the canvas and the soft tint it separates. `border-2`
 *      on both states so selecting causes no reflow. NOT `--color-cta-edge`:
 *      the accent is the color of the one button that takes the money, and a
 *      card you merely picked must not wear it. See design-system.md →
 *      "The selection family".
 *   2. the `--color-selected-soft` fill, stepped toward the canvas until it
 *      clears 1.20:1 rather than being a fixed 8% wash (the old
 *      `--color-cta-soft` measured ~1.06:1 on some brands). Still decorative,
 *      still never the signal on its own.
 *   3. a check disc — a SHAPE difference, so it survives 0% colour perception
 *      and greyscale printing.
 *   4. `font-medium` on the label.
 * Plus `data-selected`, which globals.css maps to a forced-colors outline —
 * Windows High Contrast throws away every one of the colours above.
 *
 * Never put alpha on the edge token. Compositing it back toward the canvas
 * undoes the contrast guard, and it fails on EVERY tenant including the
 * platform default (5.17:1 solid, 1.80:1 at 40%).
 *
 * `ring-offset-2` carries its own colour for the same reason. Tailwind's
 * default `--tw-ring-offset-color` is a literal `#fff`, so on any tenant whose
 * canvas is not white the focus ring would sit inside a white halo — a
 * hardcoded colour smuggled in by a utility that never names one. The offset is
 * pinned to the canvas token, matching `HeroScrollCue`.
 */

import { Check } from 'lucide-react'
import { money } from '../_lib/money'

const RADIUS = 'rounded-[var(--layout-button-radius,var(--brand-radius,0.5rem))]'

export function OptionCard({
  control,
  name,
  label,
  priceDelta,
  selected,
  onSelect,
}: {
  /** `radio` inside a step's fieldset; `checkbox` for an opt-in add-on. */
  control: 'radio' | 'checkbox'
  /** Groups the radios of one slot. Ignored by checkboxes. */
  name: string
  label: string
  /** Signed, whole dollars, as authored. 0 renders no price at all. */
  priceDelta: number
  selected: boolean
  /** The customer activated this card. The parent decides what that means —
   *  a radio picks, an add-on toggles back off. */
  onSelect: () => void
}) {
  return (
    <label
      data-selected={selected}
      className={`build-option flex cursor-pointer items-center gap-3 ${RADIUS} border-2 px-4 py-3 text-sm transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-cta-edge)] has-[:focus-visible]:ring-offset-2 has-[:focus-visible]:ring-offset-[var(--color-bg-primary)] ${
        selected
          ? 'border-[var(--color-selected)] bg-[var(--color-selected-soft)] font-medium text-[var(--color-text-primary)]'
          : 'border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-border-emphasis)]'
      }`}
    >
      <input
        type={control}
        name={name}
        checked={selected}
        onChange={onSelect}
        className="sr-only"
      />
      <span
        aria-hidden="true"
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
          selected ? 'border-[var(--color-selected)]' : 'border-[var(--color-border)]'
        }`}
      >
        {selected && <Check className="h-3 w-3 text-[var(--color-selected-readable)]" strokeWidth={3} />}
      </span>
      <span className="flex-1">{label}</span>
      {priceDelta !== 0 && (
        <span className="shrink-0 tabular-nums text-[var(--color-text-secondary)]">
          {priceDelta > 0 ? '+' : '−'}
          {money(Math.abs(priceDelta))}
        </span>
      )}
    </label>
  )
}
